import type { PlanType } from "@brookbush/database";

import { useAuth } from "@clerk/nextjs";
import type Stripe from "stripe";
import useSWR, { mutate as mutateGlobal } from "swr";

import useStripeCustomer from "./useStripeCustomer";
import fetcher from "~/utils/fetcher.client";

const useStripePaymentMethods = (planType?: PlanType) => {
	const { userId } = useAuth();

	const { customer, isLoadingCustomer } = useStripeCustomer();

	const {
		data: paymentMethods,
		mutate,
		error,
		isLoading,
	} = useSWR<Stripe.PaymentMethod[]>(
		userId &&
			(planType
				? `/stripe/payment-methods?planType=${planType}`
				: "/stripe/payment-methods"),
		{
			errorRetryCount: 0,
			revalidateOnFocus: false,
		},
	);

	const paymentMethodList = Array.isArray(paymentMethods) ? paymentMethods : [];

	const getPaymentMethod = (paymentMethodId: string) => {
		return paymentMethodList.find((item) => item.id === paymentMethodId);
	};

	const updatePaymentMethod = async (paymentMethodId: string) => {
		await mutate(
			fetcher(`/stripe/payment-methods/${paymentMethodId}`, {
				method: "PATCH",
			}),
			{
				populateCache: false,
				revalidate: true,
			},
		);

		mutateGlobal(`/stripe/payment-methods`);
		mutateGlobal(`/stripe/customer`);
	};

	const removePaymentMethod = async (paymentMethodId: string) => {
		await mutate(
			fetcher(`/stripe/payment-methods/${paymentMethodId}`, {
				method: "DELETE",
			}),
			{
				revalidate: true,
				populateCache: false,
			},
		);

		mutateGlobal(`/stripe/payment-methods`);
		mutateGlobal(`/stripe/customer`);
	};

	const defaultPaymentMethod = paymentMethodList.find(
		(paymentMethod) =>
			paymentMethod.id === customer?.invoice_settings.default_payment_method ||
			paymentMethod.id === customer?.default_source,
	);

	return {
		paymentMethods: paymentMethodList,
		isLoading: isLoading || isLoadingCustomer,
		isLoadingPaymentMethods: isLoading || isLoadingCustomer,
		error,
		getPaymentMethod,
		defaultPaymentMethod,
		updatePaymentMethod,
		removePaymentMethod,
		refreshPaymentMethods: mutate,
	};
};

export default useStripePaymentMethods;
